import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider } from './auth/AuthContext';
import LoginPage from './components/auth/LoginPage';
import WorkViewUpsert from './components/admin/workView/upsertWorkView/WorkViewUpsert';
import EmployeeProvider from './components/global/EmployeeProvider';
import Layout from './components/Layout';
import Home from './components/Home';
import Intake from './components/intake/Intake';
import Photography from './components/photography/Photography';
import Opener from './components/opener/Opener';
import Marking from './components/marking/Marking';
import Estimator from './components/estimator/Estimator';
import MassReceive from './components/massReceive/MassReceive';
import './components/app.css';
import ErrorMessage from './components/global/ErrorMessage';
import RepairLookup from './components/repairLookup/RepairLookup';
import LocationRepairs from './components/repairLookup/LocationRepairs';
import MetalCollection from './components/metalCollection/MetalCollection';
import Packer from './components/packer/Packer';
import CourierPickup from './components/courier/CourierPickup';
import CourierDropoff from './components/courier/CourierDropoff';
import Courier from './components/courier/Courier';
import Sizing from './components/sizing/Sizing';
import UserUpsert from './components/admin/user/UserUpsert';
import ViewUsers from './components/admin/user/ViewUsers';
import MissingPage from './components/global/MissingPage';
import AllCompanies from './components/admin/companies/AllCompanies';
import AllStores from './components/admin/stores/AllStores';
import Company from './components/admin/company/Company';
import Store from './components/admin/store/Store';
import ViewAllRules from './components/policyConfigurator/displayRules/ViewAllRules';
import UpsertRule from './components/policyConfigurator/upsertRules/UpsertRule';
import MetalBins from './components/metalCart/MetalBins';
import ScanLookup from './components/scans/ScanLookup';
import { POLICYCONFIGURATORSECTIONS } from './components/policyConfigurator/shared/Utilities/PolicyConfiguratorInterfaces';
import UpsertDestinationBins from './components/policyConfigurator/destinationBins/UpsertDestinationBins';
import AllDestinationBins from './components/policyConfigurator/destinationBins/AllDestinationBins';
import SheetModal from './components/admin/stores/BulkImport/SheetModal';
import Jeweler from './components/jeweler/Jeweler';
import Engraver from './components/engraver/Engraver';
import ScreenlessScannerLookup from './components/scanStations/screenless/ScreenlessScannerLookup';
import Scanner from './components/scanStations/screenless/Scanner';
import SkillLevelType from './components/skillLevelType/SkillLevelType';
import AllJobs from './components/admin/job/AllJobs';
import JobUpsert from './components/admin/job/JobUpsert';
import Wayfinder from './components/wayfinder/Wayfinder';
import Watches from './components/watches/Watches';
import WorkReassignmentLandingPage from './components/workReassignment/workReassignmentLanding';
import RepairDetail from './components/repairLookup/RepairDetail';
import RepairRejectReasons from './components/repairRejectReason/RepairRejectReasons';
import WayfinderBin from './components/repairLookup/WayfinderBin';
import BoxDetail from './components/boxLookup/BoxDetail';
import ViewWorkView from './components/admin/workView/ViewWorkViews';
import AllWorkViews from './components/admin/workView/upsertWorkView/AllWorkViews';
import applicationInsights from './lib/applicationInsights';
import RepairCost from './components/admin/costToRepair/RepairCost';

const handleError = (error: Error, info: { componentStack: string }) => {
  applicationInsights.trackException({
    exception: error,
    properties: { info: info.componentStack },
  });
};

const App: React.FC = () => {
  useEffect(() => {
    const history = createBrowserHistory();
    history.listen((update) => {
      applicationInsights.trackPageView({
        name: `${update.location.pathname}${update.location.search}`,
      });
    });
  }, []);

  return (
    <AuthProvider>
      <EmployeeProvider>
        <ErrorBoundary FallbackComponent={ErrorMessage} onError={handleError}>
          {/* the routes with barcode params have to be in here twice because
            react router v6 dropped support for optional params:
            https://github.com/remix-run/react-router/issues/7285 */}
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Executive Routes & Policy */}
            <Route path="/pricing-rules/:defaultTabParam" element={<Layout><ViewAllRules key="pricing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PRICING} /></Layout>} />
            <Route path="/pricing-rules/" element={<Layout><ViewAllRules key="pricing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PRICING} /></Layout>} />
            <Route path="/create-pricing-rule" element={<Layout><UpsertRule key="pricing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PRICING} /></Layout>} />
            <Route path="/edit-pricing-rule" element={<Layout><UpsertRule key="pricing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PRICING} isEdit /></Layout>} />

            <Route path="/marking-rules" element={<Layout><ViewAllRules key="marking" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKING} /></Layout>} />
            <Route path="/create-marking-rule" element={<Layout><UpsertRule key="marking" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKING} /></Layout>} />
            <Route path="/edit-marking-rule" element={<Layout><UpsertRule key="marking" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKING} isEdit /></Layout>} />

            <Route path="/routing-rules" element={<Layout><ViewAllRules key="routing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.ROUTING} /></Layout>} />
            <Route path="/create-routing-rule" element={<Layout><UpsertRule key="routing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.ROUTING} /></Layout>} />
            <Route path="/edit-routing-rule" element={<Layout><UpsertRule key="routing" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.ROUTING} isEdit /></Layout>} />

            <Route
              path="/markingDynamicBehavior-rules"
              element={<Layout><ViewAllRules key="markingDynamicBehavior" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKINGDYNAMICBEHAVIOR} /></Layout>}
            />
            <Route
              path="/create-markingDynamicBehavior-rule"
              element={<Layout><UpsertRule key="markingDynamicBehavior" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKINGDYNAMICBEHAVIOR} /></Layout>}
            />
            <Route
              path="/edit-markingDynamicBehavior-rule"
              element={<Layout><UpsertRule key="markingDynamicBehavior" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.MARKINGDYNAMICBEHAVIOR} isEdit /></Layout>}
            />

            <Route path="/photographyDynamic-rules" element={<Layout><ViewAllRules key="photographyDynamic" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PHOTOGRAPHYDYNAMIC} /></Layout>} />
            <Route
              path="/create-photographyDynamic-rule"
              element={(
                <Layout>
                  <UpsertRule
                    key="photographyDynamic"
                    policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PHOTOGRAPHYDYNAMIC}
                  />
                </Layout>
                )}
            />
            <Route
              path="/edit-photographyDynamic-rule"
              element={
                <Layout><UpsertRule key="photographyDynamic" policyConfiguratorSection={POLICYCONFIGURATORSECTIONS.PHOTOGRAPHYDYNAMIC} isEdit /></Layout>
                            }
            />

            {/* Admin Routes */}
            <Route path="/configureWorkViews" element={<Layout><AllWorkViews /></Layout>} />
            <Route path="/create-WorkView" element={<Layout><WorkViewUpsert /></Layout>} />
            <Route path="/update-WorkView" element={<Layout><WorkViewUpsert /></Layout>} />
            <Route path="/update-WorkView/:workViewIdParam" element={<Layout><WorkViewUpsert /></Layout>} />

            <Route path="/work-view" element={<Layout><ViewWorkView /></Layout>} />

            <Route path="/repair-cost" element={<Layout><RepairCost /></Layout>} />

            <Route path="/repair-lookup" element={<Layout><RepairLookup /></Layout>} />
            <Route path="/repair-lookup/repairs-at-wayfinder" element={<Layout><WayfinderBin /></Layout>} />

            <Route path="/repair-detail" element={<Layout><RepairDetail /></Layout>} />
            <Route path="/repair-detail/:repairBarcode" element={<Layout><RepairDetail /></Layout>} />

            <Route path="/repair-reject-reasons" element={<Layout><RepairRejectReasons /></Layout>} />
            <Route path="/repair-locations" element={<Layout><LocationRepairs /></Layout>} />

            <Route path="/view-users" element={<Layout><ViewUsers /></Layout>} />
            <Route path="/edit-user/:userId" element={<Layout><UserUpsert /></Layout>} />
            <Route path="/create-user" element={<Layout><UserUpsert /></Layout>} />

            <Route path="/companies" element={<Layout><AllCompanies /></Layout>} />
            <Route path="/company/:companyIdParam" element={<Layout><Company /></Layout>} />
            <Route path="/company/" element={<Layout><Company /></Layout>} />

            <Route path="/jobs" element={<Layout><AllJobs /></Layout>} />
            <Route path="/job/:jobIdParam" element={<Layout><JobUpsert /></Layout>} />
            <Route path="/job/" element={<Layout><JobUpsert /></Layout>} />

            <Route path="/sheet" element={<Layout><SheetModal /></Layout>} />

            <Route path="/stores" element={<Layout><AllStores /></Layout>} />
            <Route path="/stores/:companyNameParam" element={<Layout><AllStores /></Layout>} />
            <Route path="/store/:storeIdParam" element={<Layout><Store /></Layout>} />
            <Route path="/store/:companyIdParam" element={<Layout><Store /></Layout>} />
            <Route path="/store/" element={<Layout><Store /></Layout>} />

            <Route path="/metal-bins" element={<Layout><MetalBins /></Layout>} />

            <Route path="/create-location" element={<Layout><UpsertDestinationBins /></Layout>} />
            <Route path="/edit-location/:binId" element={<Layout><UpsertDestinationBins /></Layout>} />
            <Route path="/locations" element={<Layout><AllDestinationBins /></Layout>} />

            <Route path="/box-detail" element={<Layout><BoxDetail /></Layout>} />
            <Route path="/box-detail/:boxTrackingNumber" element={<Layout><BoxDetail /></Layout>} />

            {/* Receiving Routes */}
            <Route path="/receive-boxes-from-carrier" element={<Layout><Intake /></Layout>} />
            <Route path="/open-box" element={<Layout><Opener /></Layout>} />
            <Route path="/mark-repair/:barcodeParam" element={<Layout><Marking hasWarranty /></Layout>} />
            <Route path="/mark-repair" element={<Layout><Marking hasWarranty /></Layout>} />
            <Route path="/estimate-repair/:barcodeParam" element={<Layout><Estimator /></Layout>} />
            <Route path="/estimate-repair" element={<Layout><Estimator /></Layout>} />
            <Route path="/photograph-repair/:barcodeParam" element={<Layout><Photography /></Layout>} />
            <Route path="/photograph-repair" element={<Layout><Photography /></Layout>} />

            <Route path="/mass-receive" element={<Layout><MassReceive /></Layout>} />

            <Route path="/courier" element={<Layout><Courier /></Layout>} />
            <Route path="/courier-pickup/:barcode" element={<Layout><CourierPickup /></Layout>} />
            <Route path="/courier-dropoff/:barcode" element={<Layout><CourierDropoff /></Layout>} />

            <Route path="/work-reassignment" element={<Layout><WorkReassignmentLandingPage /></Layout>} />

            {/* Sizing Routes */}
            <Route path="/metal-collection" element={<Layout><MetalCollection /></Layout>} />
            <Route path="/sizing" element={<Layout><Sizing /></Layout>} />
            <Route path="/packer" element={<Layout><Packer /></Layout>} />

            {/* Other Routes */}
            <Route path="/jeweler" element={<Layout><Jeweler /></Layout>} />
            <Route path="/engraver" element={<Layout><Engraver /></Layout>} />
            <Route path="/watches" element={<Layout><Watches /></Layout>} />

            <Route path="/item-photo-view" element={<Layout routeWithoutAuth><Wayfinder /></Layout>} />

            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/scan" element={<Layout><ScanLookup /></Layout>} />
            <Route path="/scanner/:scannerIdParam" element={<Layout><Scanner /></Layout>} />
            <Route path="/scanner/" element={<Layout><Scanner /></Layout>} />
            <Route path="/screenless-scanners" element={<Layout><ScreenlessScannerLookup /></Layout>} />
            <Route path="/skill-level-types" element={<Layout><SkillLevelType /></Layout>} />
            <Route path="*" element={<Layout><MissingPage /></Layout>} />
          </Routes>
        </ErrorBoundary>
      </EmployeeProvider>
    </AuthProvider>
  );
};

export default App;
